import { useMemo, useState } from 'react';

/**
 * A Pro's availability, read in whichever time zone you want.
 *
 * A pro states their availability in their own days: "Saturday morning" means
 * Saturday morning where they are. Someone reading from another zone can't take
 * that at face value — Craig's Saturday morning in Vancouver is Saturday evening
 * in London, and his Saturday can be two different dates for a client in Tokyo.
 *
 * So the default is the READER'S zone, because that's the one they'll turn up
 * in, with a toggle to see it as the vendor states it. Both are shown labelled;
 * neither is presented as the plain truth, because neither is.
 */
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A half-day as clock hours, in the vendor's own terms. */
const SLOT_HOURS = { am: [8, 12], pm: [12, 20] };

export function viewerZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

/** Short zone label, e.g. "PDT" — what someone actually recognises. */
export function zoneLabel(zone, on = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' }).formatToParts(on);
    return parts.find((p) => p.type === 'timeZoneName')?.value || zone;
  } catch { return zone; }
}

/**
 * A wall-clock hour in `fromZone` on `isoDate`, rendered in `toZone`. Returns
 * both the time and whether it lands on a different calendar day, because that
 * shift is the whole reason this control exists.
 */
export function convertHour(isoDate, hour, fromZone, toZone) {
  const [y, m, d] = String(isoDate).split('-').map(Number);
  if (!y) return null;
  // Find the UTC instant whose local time in fromZone is exactly y-m-d hour:00.
  // Guessing UTC then correcting by the observed offset handles DST without a
  // library, and is exact for whole hours.
  let guess = Date.UTC(y, m - 1, d, hour, 0, 0);
  for (let i = 0; i < 2; i += 1) {
    const seen = new Date(guess).toLocaleString('en-US', { timeZone: fromZone, hour12: false });
    const asUtc = Date.parse(`${seen} UTC`.replace(',', ''));
    if (Number.isNaN(asUtc)) break;
    guess += Date.UTC(y, m - 1, d, hour, 0, 0) - asUtc;
  }
  const at = new Date(guess);
  const time = at.toLocaleTimeString('en-US', { timeZone: toZone, hour: 'numeric', minute: '2-digit' });
  const dayIn = (z) => at.toLocaleDateString('en-CA', { timeZone: z });
  return { time, shiftsDay: dayIn(toZone) !== dayIn(fromZone), date: dayIn(toZone) };
}

function formatDate(iso, zone, vendorZone) {
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y) return iso;
  if (zone === vendorZone) {
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
  // Anchor at midday so the label can't be dragged onto the wrong day by an
  // hour of offset — the day boundary is what we're trying to report honestly.
  const noon = convertHour(iso, 12, vendorZone, zone);
  if (!noon) return iso;
  const [cy, cm, cd] = noon.date.split('-').map(Number);
  return new Date(cy, cm - 1, cd).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function AvailabilityView({ weekly = [], dates = [], vendorTimeZone, vendorName = 'them' }) {
  const reader = useMemo(viewerZone, []);
  const vendorZone = vendorTimeZone || reader;
  const zonesDiffer = vendorZone !== reader;

  // Default to the reader's own zone — it's the one they'll show up in.
  const [showVendorZone, setShowVendorZone] = useState(false);
  const zone = showVendorZone ? vendorZone : reader;

  const closed = useMemo(
    () => (dates || []).filter((d) => !d.am && !d.pm).sort((a, b) => a.date.localeCompare(b.date)),
    [dates],
  );

  // A reference date for the weekly grid's hours — the next occurrence of each
  // weekday, so DST is right rather than assumed.
  const referenceFor = (dayOfWeek) => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    while (d.getDay() !== dayOfWeek) d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const slotLabel = (dayOfWeek, slot) => {
    const [from, to] = SLOT_HOURS[slot];
    if (!zonesDiffer || showVendorZone) return `${from % 12 || 12}${from < 12 ? 'am' : 'pm'}–${to % 12 || 12}${to < 12 ? 'am' : 'pm'}`;
    const iso = referenceFor(dayOfWeek);
    const a = convertHour(iso, from, vendorZone, zone);
    const b = convertHour(iso, to, vendorZone, zone);
    if (!a || !b) return null;
    return `${a.time}–${b.time}${a.shiftsDay || b.shiftsDay ? ' (next day)' : ''}`;
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-xs font-bold uppercase tracking-widest">Availability</span>
        <span className="font-mono text-[10px] uppercase tracking-wide text-gray-500">
          Times in {zoneLabel(zone)}
        </span>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {DAYS.map((label, i) => {
          const a = (weekly || []).find((x) => x.dayOfWeek === i);
          return (
            <div key={i}>
              <div className="mb-1 font-mono text-[10px] font-bold uppercase text-gray-500">{label}</div>
              {['am', 'pm'].map((slot) => (
                <div
                  key={slot}
                  title={a && a[slot] ? slotLabel(i, slot) || '' : 'Not available'}
                  className="mb-1 border-2 py-1 font-mono text-[9px] font-bold uppercase"
                  style={a && a[slot]
                    ? { background: '#FF00FF', borderColor: '#FF00FF', color: '#000' }
                    : { borderColor: '#e5e5e5', color: '#d1d5db' }}
                >
                  {slot}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Only offered when it would actually change what you read. */}
      {zonesDiffer && (
        <label className="mt-3 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={showVendorZone}
            onChange={(e) => setShowVendorZone(e.target.checked)}
            className="h-4 w-4 border-2 border-black accent-black"
          />
          <span className="font-mono text-[10px] uppercase tracking-wide text-gray-600">
            Display dates in {vendorName}&apos;s time zone ({zoneLabel(vendorZone)})
          </span>
        </label>
      )}

      {closed.length > 0 && (
        <div className="mt-4 border-t-2 border-black pt-3">
          <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500">
            Not available
          </div>
          <div className="flex flex-wrap gap-1.5">
            {closed.slice(0, 24).map((d) => (
              <span
                key={d.date}
                className="border-2 border-gray-300 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wide text-gray-400 line-through"
              >
                {formatDate(d.date, zone, vendorZone)}
              </span>
            ))}
            {closed.length > 24 && (
              <span className="px-1 py-1 font-mono text-[10px] uppercase tracking-wide text-gray-400">
                +{closed.length - 24} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
